import React, { useState, useEffect } from 'react';
import LandingPage from './components/LandingPage';
import QuestionnairePage from './components/QuestionnairePage';
import CompletionPage from './components/CompletionPage';
import { authenticateUser, loadQuestions, saveResponse, checkExistingResponse } from './utils/dataHandler';

function App() {
  const [currentPage, setCurrentPage] = useState('landing');
  const [userInfo, setUserInfo] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingResume, setPendingResume] = useState(null); // Store resume data until questions load

  useEffect(() => {
    // Load questions on app start
    const loadQuestionsData = async () => {
      try {
        const questionsData = await loadQuestions();
        setQuestions(questionsData);
      } catch (err) {
        console.error('Error loading questions:', err);
        setError('Failed to load questionnaire data');
      }
    };
    loadQuestionsData();
  }, []);

  // Handle resume logic when questions are loaded
  useEffect(() => {
    if (questions.length > 0 && pendingResume) {
      const { loadedResponses } = pendingResume;
      
      // Find the first unanswered question to resume from
      const firstUnansweredIndex = questions.findIndex(q => {
        const questionKey = `${q.category}|||${q.question}`;
        return loadedResponses[questionKey] === undefined;
      });
      if (firstUnansweredIndex !== -1) {
        setCurrentQuestionIndex(firstUnansweredIndex);
        console.log('Resuming from question index:', firstUnansweredIndex);
      } else {
        // All questions answered, go to last question
        setCurrentQuestionIndex(questions.length - 1);
        console.log('All questions answered, going to last question');
      }
      
      setPendingResume(null); // Clear pending resume data
    }
  }, [questions, pendingResume]);

  const handleAuthentication = async (companyCode, employeeName, questionnaireCode) => {
    setLoading(true);
    setError('');
    
    try {
      // Check if user has existing responses
      const existingResponse = await checkExistingResponse(companyCode, employeeName, questionnaireCode);
      
      if (existingResponse && existingResponse.isCompleted) {
        setError('Questionnaire is already completed, Thank you');
        setLoading(false);
        return;
      }

      // Authenticate user
      const authResult = await authenticateUser(companyCode, employeeName, questionnaireCode);
      if (authResult.success) {
        setUserInfo({
          companyCode,
          employeeName,
          questionnaireCode
        });
        
        // Load existing partial responses if they exist
        if (existingResponse && existingResponse.responses) {
          const loadedResponses = {};
          existingResponse.responses.forEach(response => {
            if (response.employeeResponse !== null && response.employeeResponse !== undefined) {
              // Create a unique key using category + question text to match responses
              const questionKey = `${response.category}|||${response.question}`;
              loadedResponses[questionKey] = response.employeeResponse;
            }
          });
          setResponses(loadedResponses);
          
          console.log('Loaded existing responses:', loadedResponses);
          
          // If questions are already loaded, find resume point immediately
          if (questions.length > 0) {
            const firstUnansweredIndex = questions.findIndex(q => {
              const questionKey = `${q.category}|||${q.question}`;
              return loadedResponses[questionKey] === undefined;
            });
            if (firstUnansweredIndex !== -1) {
              setCurrentQuestionIndex(firstUnansweredIndex);
              console.log('Resuming from question index:', firstUnansweredIndex);
            } else {
              setCurrentQuestionIndex(questions.length - 1);
            }
          } else {
            // Store resume data for when questions load
            setPendingResume({ loadedResponses });
          }
        }
        
        setCurrentPage('questionnaire');
      } else {
        setError(authResult.error);
      }
    } catch (err) {
      console.error('Authentication error:', err);
      setError('Authentication failed. Please try again.');
    }
    
    setLoading(false);
  };

  const handleQuestionResponse = (question, response) => {
    // Create a unique key using category + question text
    const questionKey = `${question.category}|||${question.question}`;
    setResponses(prev => ({
      ...prev,
      [questionKey]: response
    }));
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      // All questions completed, save responses and show completion
      handleCompleteQuestionnaire();
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleCompleteQuestionnaire = async () => {
    setLoading(true);
    try {
      await saveResponse(userInfo, questions, responses, false); // Final save
      setCurrentPage('completion');
    } catch (err) {
      console.error('Error saving responses:', err);
      setError('Failed to save responses. Please try again.');
    }
    setLoading(false);
  };

  const handleSaveAndContinue = async () => {
    setLoading(true);
    try {
      await saveResponse(userInfo, questions, responses, true); // Partial save
      setError(''); // Clear any previous errors
      
      // Show prominent success message briefly and return immediately
      const successMessage = 'Your answers are saved';
      setError(successMessage);
      
      // Return to landing page immediately
      setTimeout(() => {
        setError('');
        handleReturnToLanding();
      }, 500); // Very brief delay just to show the message
      
    } catch (err) {
      console.error('Error saving partial responses:', err);
      setError('Failed to save progress. Please try again.');
    }
    setLoading(false);
  };

  const handleReturnToLanding = () => {
    setCurrentPage('landing');
    setUserInfo(null);
    setCurrentQuestionIndex(0);
    setResponses({});
    setError('');
    setPendingResume(null);
  };

  const getCompletedCategories = () => {
    const categories = [...new Set(questions.map(q => q.category))];
    return categories.filter(category => {
      const categoryQuestions = questions.filter(q => q.category === category);
      return categoryQuestions.every(q => {
        const questionKey = `${q.category}|||${q.question}`;
        return responses[questionKey] !== undefined;
      });
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      {error && (
        <div className={`fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 px-8 py-6 rounded-lg shadow-2xl z-50 text-center ${
          error.includes('Your answers are saved') 
            ? 'bg-green-600 text-white text-xl font-semibold' 
            : error.includes('successfully') || error.includes('saved') 
            ? 'bg-green-500 text-white' 
            : 'bg-red-500 text-white'
        }`}>
          {error}
        </div>
      )}
      
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Processing...</p>
          </div>
        </div>
      )}

      {currentPage === 'landing' && (
        <LandingPage 
          onAuthenticate={handleAuthentication}
          loading={loading}
        />
      )}

      {currentPage === 'questionnaire' && questions.length > 0 && (
        <QuestionnairePage
          question={questions[currentQuestionIndex]}
          currentIndex={currentQuestionIndex}
          totalQuestions={questions.length}
          response={responses[`${questions[currentQuestionIndex]?.category}|||${questions[currentQuestionIndex]?.question}`]}
          onResponse={handleQuestionResponse}
          onNext={handleNextQuestion}
          onPrevious={handlePreviousQuestion}
          onSaveAndContinue={handleSaveAndContinue}
          completedCategories={getCompletedCategories()}
          allCategories={[...new Set(questions.map(q => q.category))]}
          userInfo={userInfo}
          loading={loading}
        />
      )}

      {currentPage === 'completion' && (
        <CompletionPage 
          onReturnToLanding={handleReturnToLanding}
          userInfo={userInfo}
        />
      )}
    </div>
  );
}

export default App;